<template>
  <v-container fluid>
    <v-toolbar class="pb-10" flat>
      <v-btn
        :to="{ name: 'TaskAnalysis', query: { dates: dates, team: selectedTeam } }"
        color="primary"
        outlined
      >
        <v-icon left>mdi-chart-timeline-variant</v-icon>
        Task Analysis
      </v-btn>
      <v-spacer/>
      <DebouncedTextField
        v-model="search"
        append-icon="mdi-magnify"
        class="d-print-none"
        clearable
        hide-details
        label="Search"
        single-line
      />
      <v-spacer/>
      <v-btn-toggle v-model="selectedTeam">
        <v-btn value="">All Teams</v-btn>
        <v-btn
          v-for="team of teamFilter"
          v-bind:key="team"
          :value="team"
        >{{ team }}
        </v-btn>
      </v-btn-toggle>

      <v-spacer/>
      <v-text-field :value="datesFormatted"
                    append-icon="mdi-calendar"
                    hint="Task Dates"
                    label="Select dates"
                    persistent-hint
                    readonly
                    single-line
                    @click="showDatePicker=true"
      />

      <v-dialog
        ref="dialog"
        v-model="showDatePicker"
        persistent
        width="290px"
      >
        <v-date-picker
          v-model="dates"
          no-title
          range
        ></v-date-picker>
        <v-btn @click="showDatePicker=false; dates.sort()">Close</v-btn>
      </v-dialog>
    </v-toolbar>

    <!-- Duration Filter Controls -->
    <v-row align="center" class="mt-2 mx-0">
      <v-col/>
      <v-col cols="auto">
        <v-checkbox
          v-model="durationFilterActive"
          class="mt-0 pt-0"
          hide-details
          label="Duration Filter"
        />
      </v-col>
      <v-col cols="auto">
        <v-text-field
          v-model.number="minDurationMinutes"
          :disabled="!durationFilterActive"
          dense
          hide-details
          label="Min (min)"
          min="0"
          step="1"
          style="max-width: 120px"
          type="number"
        />
      </v-col>
      <v-col cols="auto">
        <v-text-field
          v-model.number="maxDurationHours"
          :disabled="!durationFilterActive"
          dense
          hide-details
          label="Max (hrs)"
          min="0"
          step="0.5"
          style="max-width: 120px"
          type="number"
        />
      </v-col>
      <v-col cols="auto">
        <span class="caption text--secondary">Exclude tasks shorter/longer than limits</span>
      </v-col>
      <v-col/>
    </v-row>

    <!-- Metrics Section -->
    <!-- Metric Titles Row -->
    <v-row class="mt-6">
      <v-col cols="12" md="4">
        <v-card flat>
          <v-card-title class="pb-0">
            Metric 1: Total Time Per Person
            <v-spacer/>
            <v-btn outlined small @click="csvExport(metric1Data, 'total-time-per-person')">CSV</v-btn>
          </v-card-title>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card flat>
          <v-card-title class="">
            Metric 2: Tasks Meeting Target
            <v-spacer/>
            <v-btn outlined small @click="csvExport(metric2Data, 'tasks-meeting-target')">CSV</v-btn>
          </v-card-title>
          <v-card-text>
            <v-row align="center">
              <v-col cols="auto">
                <v-text-field
                  v-model.number="targetTolerance"
                  dense
                  hide-details
                  max="100"
                  min="0"
                  outlined
                  step="5"
                  style="max-width: 80px"
                  suffix="%"
                  type="number"
                />
              </v-col>
              <v-col>
              <span class="caption text--secondary">
                Tasks up to {{ targetTolerance }}% slower than estimate are considered on target
              </span>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card flat>
          <v-card-title class="pb-0">
            Metric 3: Daily Performance vs Target
            <v-spacer/>
            <v-btn outlined small @click="csvExport(metric3Data, 'daily-performance-vs-target')">CSV</v-btn>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>

    <!-- Metric Content Row -->
    <v-row>
      <!-- Metric 1: Total time spent on task, per person -->
      <v-col cols="12" md="4">
        <v-card flat>
          <v-card-text class="pt-0">
            <v-data-table
              :headers="metric1Headers"
              :items="metric1Data"
              :loading="loading"
              :search="search"
              dense
              disable-pagination
              hide-default-footer
              sort-by="person"
            >
              <template v-slot:item.person="{ item }">
                <a style="cursor: pointer; text-decoration: underline; color: #1976d2;"
                   @click="showPersonTaskDetails(item.person)">
                  {{ item.person }}
                </a>
              </template>
              <template v-slot:item.totalHours="{ item }">
                {{ item.totalHours.toFixed(2) }}
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Metric 2: % of tasks meeting target -->
      <v-col cols="12" md="4">
        <v-card flat>
          <v-card-text class="pt-0">
            <v-data-table
              :headers="metric2Headers"
              :items="metric2Data"
              :loading="loading"
              :search="search"
              dense
              disable-pagination
              hide-default-footer
              sort-by="person"
            >
              <template v-slot:item.person="{ item }">
                <a style="cursor: pointer; text-decoration: underline; color: #1976d2;"
                   @click="showPersonTaskDetails(item.person)">
                  {{ item.person }}
                </a>
              </template>
              <template v-slot:item.percentageMeetingTarget="{ item }">
                {{ item.percentageMeetingTarget.toFixed(1) }}%
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Metric 3: Total day's tasks compared to target -->
      <v-col cols="12" md="4">
        <v-card flat>
          <v-card-text class="pt-0">
            <v-data-table
              :headers="metric3Headers"
              :items="metric3Data"
              :loading="loading"
              :search="search"
              dense
              disable-pagination
              hide-default-footer
              sort-by="person"
            >
              <template v-slot:item.person="{ item }">
                <a style="cursor: pointer; text-decoration: underline; color: #1976d2;"
                   @click="showPersonTaskDetails(item.person)">
                  {{ item.person }}
                </a>
              </template>
              <template v-slot:item.totalActualHours="{ item }">
                {{ item.totalActualHours.toFixed(2) }}
              </template>
              <template v-slot:item.totalEstimatedHours="{ item }">
                {{ item.totalEstimatedHours.toFixed(2) }}
              </template>
              <template v-slot:item.performanceRatio="{ item }">
                {{ item.performanceRatio.toFixed(2) }}
              </template>
              <template v-slot:item.performancePercentage="{ item }">
                <v-chip
                  :color="item.performancePercentage > 100 ? 'green' : 'red'"
                  small
                  text-color="white"
                >
                  {{ item.performancePercentage.toFixed() }}%
                </v-chip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Task Details Dialog -->
    <v-dialog v-model="showTaskDetailsDialog" fullscreen>
      <v-card>
        <v-card-title>
          Task Details for {{ selectedPerson }}
          <v-spacer/>
          <v-btn icon @click="showTaskDetailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <ChoppingTaskTable
            :headers="taskDetailsHeaders"
            :tasks="selectedPersonTasks"
            :teams="teams"
            sort-by="person"
          >
            <template v-slot:actions="{ item }">
              <v-btn
                :to="getTaskAnalysisRoute(item)"
                color="primary"
                outlined
                small
              >
                <v-icon left small>mdi-chart-timeline-variant</v-icon>
                Analysis
              </v-btn>
            </template>
          </ChoppingTaskTable>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="primary" @click="showTaskDetailsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from '@/api';
import moment from 'moment';
import urlState from "../router/urlState";
import {
  calculateActualRate,
  calculateEstimatedDuration,
  calculatePerformance,
  calculateTotalWeight,
  csvExport as utilsCsvExport,
  formatDuration,
  getEstimatedRate
} from '@/utils/tasks/chopping-utils.js';
import ChoppingTaskTable from '@/components/ChoppingTaskTable.vue';
import DebouncedTextField from "@/components/DebouncedTextField.vue";

export default {
  name: "TaskReport2",
  components: {
    ChoppingTaskTable,
    DebouncedTextField
  },
  data() {
    return {
      loading: false,
      showDatePicker: null,
      dates: [],
      search: null,
      selectedTeam: '', // Selected team filter
      minDurationMinutes: 1, // Only show tasks longer than N minutes (default 1)
      maxDurationHours: 6, // Only show tasks shorter than N hours (default 4)
      durationFilterActive: true, // Whether duration filter is active (default true)
      targetTolerance: 25, // Percentage tolerance for considering tasks "on target" (default 25%)
      teamFilter: [],
      rawTasks: [], // Store the raw task data
      teams: {}, // Map of team_id to team name

      // Task details dialog
      showTaskDetailsDialog: false,
      selectedPerson: '',
      selectedPersonTasks: [],

      // Metric 1 headers
      metric1Headers: [
        {text: 'Person', value: 'person'},
        {text: 'Total Hours', value: 'totalHours'},
        {text: 'Days Worked', value: 'daysWorked'}
      ],

      // Metric 2 headers
      metric2Headers: [
        {text: 'Person', value: 'person'},
        {text: 'Too Short', value: 'tooShort'},
        {text: 'Too Long', value: 'tooLong'},
        {text: 'Total Tasks', value: 'totalTasks'},
        {text: 'Tasks Meeting Target', value: 'tasksMeetingTarget'},
        {text: '% Meeting Target', value: 'percentageMeetingTarget'}
      ],

      // Metric 3 headers
      metric3Headers: [
        {text: 'Person', value: 'person'},
        {text: 'Actual Hours', value: 'totalActualHours'},
        {text: 'Estimated Hours', value: 'totalEstimatedHours'},
        {text: 'Performance Ratio', value: 'performanceRatio'},
        {text: 'Performance %', value: 'performancePercentage'}
      ],

      // Task details dialog headers
      taskDetailsHeaders: [
        {text: 'Task', value: 'title'},
        {text: 'Date', value: 'date'},
        {text: 'Task Times', value: 'executionTimes'},
        {text: 'Total Weight', value: 'totalWeight'},
        {text: 'Duration', value: 'duration'},
        {text: 'Actual Rate (kg/hr)', value: 'actualRate'},
        {text: 'Estimated Duration', value: 'estimatedDuration'},
        {text: 'Estimated Rate (kg/hr)', value: 'apiRate'},
        {text: 'Performance', value: 'performance'},
        {text: 'Description', value: 'description'},
        {text: 'Actions', value: 'actions', sortable: false}
      ]
    }
  },
  mixins: [urlState],
  mounted() {

    this.syncToUrl({param: 'selectedTeam', urlParam: 'team', initFromRoute: true});
    this.syncToUrl({param: 'maxDurationHours', urlParam: 'max_duration', initFromRoute: true});
    this.syncToUrl({
      param: 'dates', urlParam: 'dates',
      initFromRoute: true
    });
    this.updateDateRange();
  },

  watch: {
    dates: 'updateDateRange'
  },

  computed: {
    // Filtered tasks based on selected team and duration
    tasks() {
      let filteredTasks = this.rawTasks;

      // Filter by team if selected
      if (this.selectedTeam) {
        filteredTasks = filteredTasks.filter(task => {
          if (task.execution && task.execution.length > 0) {
            return task.execution.some(execution => {
              if (execution.team_members) {
                return execution.team_members.some(member => {
                  const teamName = this.teams[member.team_id] || member.team_id;
                  return teamName === this.selectedTeam;
                });
              }
              return false;
            });
          }
          return false;
        });
      }

      // Filter by duration range (if filter is active)
      if (this.durationFilterActive) {
        filteredTasks = filteredTasks.filter(task => {
          const taskDurationHours = this.msToHours(task.elapsed || 0);
          const taskDurationMinutes = taskDurationHours * 60;

          // Check minimum duration
          if (this.minDurationMinutes > 0 && taskDurationMinutes < this.minDurationMinutes) {
            return false;
          }

          // Check maximum duration
          if (this.maxDurationHours > 0 && taskDurationHours >= this.maxDurationHours) {
            return false;
          }

          return true;
        });
      }

      return filteredTasks;
    },

    datesFormatted() {
      const format = 'dddd MMMM D';
      const from = this.dates[0];
      const to = this.dates[1];
      if (!from) {
        return '';
      } else {
        if (from === to) {
          return `${moment(from).format(format)}`;
        } else {
          return `${moment(from).format(format)} - ${moment(to).format(format)}`
        }
      }
    },

    // Metric 1: Total time spent on task, per person
    metric1Data() {
      const personData = {};

      for (const task of this.tasks) {
        if (task.execution && task.execution.length > 0) {
          for (const execution of task.execution) {
            const {start, stop, team_members} = execution;
            if (start && stop && team_members) {
              const duration = this.getDurationInHours(start, stop);
              const workDate = moment(start).format('YYYY-MM-DD');

              for (const member of team_members) {
                if (!personData[member.name]) {
                  personData[member.name] = {
                    totalHours: 0,
                    workDates: new Set()
                  };
                }
                personData[member.name].totalHours += duration / team_members.length; // Split time among team members
                personData[member.name].workDates.add(workDate);
              }
            }
          }
        }
      }

      return Object.entries(personData).map(([person, data]) => ({
        person,
        totalHours: data.totalHours,
        daysWorked: data.workDates.size
      }));
    },

    // Metric 2: % of tasks meeting target
    metric2Data() {
      const personStats = {};
      const personStatsUnfiltered = {};
      const personFilteredStats = {};

      // First pass: calculate stats for all tasks (before duration filtering) and categorize filtered ones
      for (const task of this.rawTasks) {
        // Apply team filter only (not duration filter)
        let includeTask = true;
        if (this.selectedTeam) {
          includeTask = false;
          if (task.execution && task.execution.length > 0) {
            for (const execution of task.execution) {
              if (execution.team_members) {
                for (const member of execution.team_members) {
                  const teamName = this.teams[member.team_id] || member.team_id;
                  if (teamName === this.selectedTeam) {
                    includeTask = true;
                    break;
                  }
                }
                if (includeTask) break;
              }
            }
          }
        }

        if (includeTask && task.execution && task.execution.length > 0) {
          // Check if task is filtered by duration
          let isTooShort = false;
          let isTooLong = false;

          if (this.durationFilterActive) {
            const taskDurationHours = this.msToHours(task.elapsed || 0);
            const taskDurationMinutes = taskDurationHours * 60;

            if (this.minDurationMinutes > 0 && taskDurationMinutes < this.minDurationMinutes) {
              isTooShort = true;
            }
            if (this.maxDurationHours > 0 && taskDurationHours >= this.maxDurationHours) {
              isTooLong = true;
            }
          }

          for (const execution of task.execution) {
            if (execution.team_members) {
              for (const member of execution.team_members) {
                // Track total unfiltered tasks
                if (!personStatsUnfiltered[member.name]) {
                  personStatsUnfiltered[member.name] = {
                    totalTasks: 0
                  };
                }
                personStatsUnfiltered[member.name].totalTasks += 1;

                // Track filtered tasks breakdown
                if (!personFilteredStats[member.name]) {
                  personFilteredStats[member.name] = {
                    tooShort: 0,
                    tooLong: 0
                  };
                }

                if (isTooShort) {
                  personFilteredStats[member.name].tooShort += 1;
                }
                if (isTooLong) {
                  personFilteredStats[member.name].tooLong += 1;
                }
              }
            }
          }
        }
      }

      // Second pass: calculate stats for filtered tasks
      for (const task of this.tasks) {
        if (task.execution && task.execution.length > 0) {
          const taskActualDuration = task.elapsed || 0;

          // Calculate estimated duration using API rate and weight from chopping-utils
          let taskEstimatedDuration = 0;
          if (task.apiRate) {
            const totalWeightKg = calculateTotalWeight(task.description) / 1000;
            const estimatedRateNum = parseFloat(task.apiRate);
            if (totalWeightKg > 0 && estimatedRateNum > 0) {
              const estimatedDurationHours = calculateEstimatedDuration(totalWeightKg, estimatedRateNum);
              taskEstimatedDuration = (estimatedDurationHours || 0) * 1000 * 60 * 60; // Convert to milliseconds
            }
          }

          // Calculate if task meets target using tolerance percentage
          let meetingTarget = false;
          if (taskEstimatedDuration > 0) {
            const toleranceMultiplier = 1 + (this.targetTolerance / 100);
            const maxAcceptableDuration = taskEstimatedDuration * toleranceMultiplier;
            meetingTarget = taskActualDuration <= maxAcceptableDuration;
          }

          for (const execution of task.execution) {
            if (execution.team_members) {
              for (const member of execution.team_members) {
                if (!personStats[member.name]) {
                  personStats[member.name] = {
                    tasksMeetingTarget: 0,
                    totalTasks: 0
                  };
                }

                personStats[member.name].totalTasks += 1;
                if (meetingTarget) {
                  personStats[member.name].tasksMeetingTarget += 1;
                }
              }
            }
          }
        }
      }

      return Object.entries(personStats).map(([person, stats]) => {
        const filteredStats = personFilteredStats[person] || {tooShort: 0, tooLong: 0};

        return {
          person,
          tasksMeetingTarget: stats.tasksMeetingTarget,
          totalTasks: stats.totalTasks,
          tooShort: filteredStats.tooShort,
          tooLong: filteredStats.tooLong,
          percentageMeetingTarget: stats.totalTasks > 0 ? (stats.tasksMeetingTarget / stats.totalTasks) * 100 : 0
        };
      });
    },

    // Metric 3: Total day's tasks compared to target
    metric3Data() {
      const personPerformance = {};

      for (const task of this.tasks) {
        if (task.execution && task.execution.length > 0) {
          const taskActualHours = this.msToHours(task.elapsed || 0);

          // Calculate estimated hours using API rate
          let taskEstimatedHours = 0;
          if (task.apiRate) {
            const totalWeightKg = calculateTotalWeight(task.description) / 1000;
            const estimatedRateNum = parseFloat(task.apiRate);
            if (totalWeightKg > 0 && estimatedRateNum > 0) {
              const estimatedDurationHours = calculateEstimatedDuration(totalWeightKg, estimatedRateNum);
              taskEstimatedHours = estimatedDurationHours || 0;
            }
          }

          for (const execution of task.execution) {
            if (execution.team_members) {
              for (const member of execution.team_members) {
                if (!personPerformance[member.name]) {
                  personPerformance[member.name] = {
                    totalActualHours: 0,
                    totalEstimatedHours: 0
                  };
                }

                // Split task time among team members for this execution
                const memberShare = 1 / execution.team_members.length;
                personPerformance[member.name].totalActualHours += taskActualHours * memberShare;
                personPerformance[member.name].totalEstimatedHours += taskEstimatedHours * memberShare;
              }
            }
          }
        }
      }

      return Object.entries(personPerformance).map(([person, performance]) => {
        const ratio = performance.totalEstimatedHours > 0
          ? performance.totalActualHours / performance.totalEstimatedHours
          : 0;

        // Use chopping-utils calculatePerformance to get consistent performance calculation
        const performanceResult = calculatePerformance(performance.totalActualHours, performance.totalEstimatedHours);
        let percentage = 0;
        if (performanceResult) {
          // Extract percentage number from text like "25.0% faster" or "15.0% slower"
          const percentageMatch = performanceResult.text.match(/(\d+\.?\d*)%/);
          if (percentageMatch) {
            const basePercentage = parseFloat(percentageMatch[1]);
            // Convert to efficiency percentage (>100% is better performance)
            percentage = performanceResult.text.includes('faster')
              ? 100 + basePercentage
              : 100 - basePercentage;
          }
        }

        return {
          person,
          totalActualHours: performance.totalActualHours,
          totalEstimatedHours: performance.totalEstimatedHours,
          performanceRatio: ratio,
          performancePercentage: percentage
        };
      });
    }
  },

  methods: {
    formatDuration,
    async fetchTeamData() {
      // Known team names based on the codebase analysis
      const knownTeams = ['chopping', 'components', 'packing', 'plating'];
      const teamPromises = knownTeams.map(async (teamName) => {
        try {
          const response = await api.get(`/v2/task/team/${teamName}`);
          return {id: response.data.id, name: teamName};
        } catch (error) {
          console.warn(`Failed to fetch team ${teamName}:`, error);
          return null;
        }
      });

      const teamResults = await Promise.all(teamPromises);
      const teams = {};

      teamResults.forEach(team => {
        if (team) {
          teams[team.id] = team.name;
        }
      });

      this.teams = teams;
      return teams;
    },

    async updateDateRange() {
      const [from, to] = this.dates;
      if (from && to) {
        // Fetch teams first if not already loaded
        if (Object.keys(this.teams).length === 0) {
          await this.fetchTeamData();
        }

        this.loading = true;
        return api
          .get(`/v2/task`, {
            params: {
              from,
              to,
              team: 1,
              includeProcessingRates: true,
              queryByExecution: true
            }
          })
          .then(result => {
            const {data: tasks} = result;
            this.rawTasks = tasks;

            // Extract unique team names for filter
            const teamNames = new Set();
            for (const task of tasks) {
              if (task.execution) {
                for (const execution of task.execution) {
                  if (execution.team_members) {
                    for (const member of execution.team_members) {
                      const teamName = this.teams[member.team_id] || member.team_id;
                      teamNames.add(teamName);
                    }
                  }
                }
              }
            }
            this.teamFilter = [...teamNames].sort();
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },

    getDurationInHours(start, stop) {
      const startTime = Date.parse(start);
      const stopTime = Date.parse(stop);
      return (stopTime - startTime) / (1000 * 60 * 60); // Convert ms to hours
    },

    msToHours(ms) {
      return ms / (1000 * 60 * 60);
    },

    getTeamName(teamId) {
      return this.teams[teamId] || `Team ${teamId}`;
    },

    showPersonTaskDetails(personName) {
      this.selectedPerson = personName;
      this.selectedPersonTasks = this.getPersonTasks(personName);
      this.showTaskDetailsDialog = true;
    },

    getPersonTasks(personName) {
      const personTasks = [];

      for (const task of this.tasks) {
        if (task.execution && task.execution.length > 0) {
          for (const execution of task.execution) {
            const {start, stop, team_members} = execution;
            if (start && stop && team_members) {
              const memberInExecution = team_members.find(member => member.name === personName);
              if (memberInExecution) {
                const duration = this.getDurationInHours(start, stop) / team_members.length; // Split time among team members
                const taskDate = moment(start).format('YYYY-MM-DD (dddd)');

                // Calculate rates and estimated duration
                const totalWeightKg = calculateTotalWeight(task.description) / 1000;
                const actualRate = calculateActualRate(totalWeightKg, duration);
                const estimatedRate = getEstimatedRate(task);
                const estimatedRateNum = task.apiRate ? parseFloat(task.apiRate) : null;
                const estimatedDurationHours = estimatedRateNum ? calculateEstimatedDuration(totalWeightKg, estimatedRateNum) : null;
                const performance = calculatePerformance(duration, estimatedDurationHours);

                personTasks.push({
                  ...task,
                  title: task.title || 'Untitled Task',
                  description: task.description || '',
                  duration: duration,
                  estimatedDuration: estimatedDurationHours ? estimatedDurationHours * 1000 * 60 * 60 : null, // Convert to milliseconds for formatDuration
                  actualRate: actualRate,
                  estimatedRate: estimatedRate,
                  performance: performance,
                  date: taskDate,
                  taskId: task.id,
                  entryType: task.entryType, // Include entryType for blue check indicator
                  task: task // Keep reference to original task for weight calculation
                });
              }
            }
          }
        }
      }

      // Sort by date (most recent first)
      return personTasks.sort((a, b) => new Date(b.date) - new Date(a.date));
    },


    csvExport(arrData, filename) {
      utilsCsvExport(arrData, `${filename}-${this.dates.join('-')}`);
    },

    getTaskAnalysisRoute(item) {
      // Get the team for this task
      const task = item.task;
      let teamName = '';

      if (task.execution && task.execution.length > 0) {
        for (const execution of task.execution) {
          if (execution.team_members && execution.team_members.length > 0) {
            const teamId = execution.team_members[0].team_id;
            teamName = this.teams[teamId] || '';
            break;
          }
        }
      }

      return {
        name: 'TaskAnalysis',
        query: {
          dates: this.dates,
          team: teamName,
          taskId: item.taskId
        }
      };
    }
  }
}
</script>

<style scoped>
</style>